import { DefaultTheme, createGlobalStyle } from 'styled-components';

import { focusOutline } from '../reusable/focus';
import { reset } from '../reset';

import { colors } from './colors';
import { THEME_COLORS } from './colorThemes';

// Font files
import '@fontsource/open-sans/latin-400.css';
import '@fontsource/open-sans/latin-400-italic.css';
import '@fontsource/open-sans/latin-700.css';
import '@fontsource/open-sans/latin-700-italic.css';
import '@fontsource/montserrat/latin-400.css';
import '@fontsource/montserrat/latin-700.css';
import '@fontsource/noto-serif/latin-400-italic.css';
import '@fontsource/noto-serif/latin-700-italic.css';

export const breakpoints = {
  xs: { value: 360, unit: 'px' },
  sm: { value: 450, unit: 'px' },
  md: { value: 800, unit: 'px' },
  lg: { value: 900, unit: 'px' },
  xl: { value: 1200, unit: 'px' },
  xxl: { value: 1400, unit: 'px' },
  xxxl: { value: 1988, unit: 'px' },
};

export const dimensions = {
  heightNav: { value: 60, unit: 'px' },
  largeHeightNav: { value: 76, unit: 'px' },
};

export const mainTheme: DefaultTheme = {
  breakpoints: {
    xs: `${breakpoints.xs.value}${breakpoints.xs.unit}`,
    sm: `${breakpoints.sm.value}${breakpoints.sm.unit}`,
    md: `${breakpoints.md.value}${breakpoints.md.unit}`,
    lg: `${breakpoints.lg.value}${breakpoints.lg.unit}`,
    xl: `${breakpoints.xl.value}${breakpoints.xl.unit}`,
    xxl: `${breakpoints.xxl.value}${breakpoints.xxl.unit}`,
    xxxl: `${breakpoints.xxxl.value}${breakpoints.xxxl.unit}`,
  },
};

export const GlobalStyles = createGlobalStyle`
  ${reset}

  :root {
    /* Colors */

    ${Object.keys(colors)
      .map(color => `--color-${color}: ${colors[color]};`)
      .join('\n')}

    /* Font families */
    --font-primary: "Open Sans", Arial, Helvetica, sans-serif;
    --font-secondary: Montserrat, Arial, Helvetica, sans-serif;
    --font-tertiary: "Noto Serif", Georgia, serif;

    /* Font sizes */
    --font-size-xs: 0.75rem;
    --font-size-sm: 0.875rem;
    --font-size-default: 1rem;
    --font-size-md: 1.125rem;
    --font-size-lg: 1.25rem;
    --font-size-xl: 1.75rem;
    --font-size-xxl: 2.25rem;
    --font-size-xxxl: 2.7625rem;

    /* Z-indexes */
    --z-index-modal-base: 200;
    --z-index-modal-overlay: 201;
    --z-index-modal-content: 202;
    --z-index-nav: 101;
    --z-index-overflow-menu: 102;
    --z-index-veggie-burger: 103;
    --z-index-notification-banner: 104;
    --z-index-pinned-or-sticky-call-to-action: 90;
    --z-index-tooltip-active: 71;
    --z-index-tooltip: 70;
    --z-index-accordion: 69;

    /* Dimensions */
    --height-nav: ${dimensions.heightNav.value}${dimensions.heightNav.unit};
    --input-texty-height: 3.125rem;
    --form-side-padding: 1.25rem;
    --form-header-side-padding: 1.25rem;
    --modal-padding: 2rem;
    --main-container-width: 600px;
    --form-container-width: 663px;

    /* Images */
    --image-inverse-chevron-yellow: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAzNTAuMjA3NTUgNTUuODIxNDciIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDM1MC4yMDc1NSA1NS44MjE0NzsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiNGRkI2MDE7fQo8L3N0eWxlPgo8cG9seWdvbiBjbGFzcz0ic3QwIiBwb2ludHM9IjAsMCAxNzUuMTAzNzgsNTUuMjAzMzcgMzUwLjIwNzU1LDAgMzUwLjIwNzU1LDU2LjgyNzk0IDAsNTYuODI3OTQgIi8+Cjwvc3ZnPgo=');
    --image-inverse-chevron-grey: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAzNTAuMiA1NS44IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAzNTAuMiA1NS44OyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+Cgkuc3Qwe2ZpbGw6I0YzRjRGNTt9Cjwvc3R5bGU+Cjxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMCwwIDE3NS4xLDU1LjIgMzUwLjIsMCAzNTAuMiw1Ni44IDAsNTYuOCAiLz4KPC9zdmc+Cg==');
    --image-inverse-chevron-white:url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAzNTAuMiA1NS44IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAzNTAuMiA1NS44OyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+Cgkuc3Qwe2ZpbGw6I0ZGRkZGRjt9Cjwvc3R5bGU+Cjxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMCwwIDE3NS4xLDU1LjIgMzUwLjIsMCAzNTAuMiw1Ni44IDAsNTYuOCAiLz4KPC9zdmc+Cg==');
    --image-select-arrow: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+PHN2ZyB3aWR0aD0iNnB4IiBoZWlnaHQ9IjEycHgiIHZpZXdCb3g9IjAgMCA2IDEyIiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPiAgICAgICAgPHRpdGxlPnNlbGVjdGFycm93PC90aXRsZT4gICAgPGRlc2M+Q3JlYXRlZCB3aXRoIFNrZXRjaC48L2Rlc2M+ICAgIDxkZWZzPjwvZGVmcz4gICAgPGcgaWQ9IlBhZ2UtMSIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4gICAgICAgIDxnIGlkPSJzZWxlY3RhcnJvdyIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMC4wMDAwMDAsIDEuMDAwMDAwKSIgc3Ryb2tlPSIjODY4RTk2Ij4gICAgICAgICAgICA8cG9seWxpbmUgaWQ9IlBhdGgiIHBvaW50cz0iMCAzIDMuMDU1MDg0OTMgMCA2IDIuODkxODE2NTciPjwvcG9seWxpbmU+ICAgICAgICAgICAgPHBvbHlsaW5lIGlkPSJQYXRoLUNvcHkiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDMuMDAwMDAwLCA4LjUwMDAwMCkgc2NhbGUoMSwgLTEpIHRyYW5zbGF0ZSgtMy4wMDAwMDAsIC04LjUwMDAwMCkgIiBwb2ludHM9IjAgMTAgMy4wNTUwODQ5MyA3IDYgOS44OTE4MTY1NyI+PC9wb2x5bGluZT4gICAgICAgIDwvZz4gICAgPC9nPjwvc3ZnPg==');
    --image-checkmark: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNOSAyMS4wMzVsLTktOC42MzggMi43OTEtMi44NyA2LjE1NiA1Ljg3NCAxMi4yMS0xMi40MzYgMi44NDMgMi44MTd6Ii8+PC9zdmc+');
    --image-heart-pulse: url("data:image/svg+xml;charset=UTF-8, %3csvg xmlns='http://www.w3.org/2000/svg' fill-rule='evenodd' stroke-linejoin='round' stroke-miterlimit='2' clip-rule='evenodd' viewBox='0 0 500 500'%3e%3cstyle%3e %40keyframes pulse %7b 0%25 %7b transform: scale(0.9); opacity: 0.8;%7d 50%25 %7b transform: scale(1.1); opacity: 1;%7d 100%25 %7b transform: scale(0.9); opacity: 0.8;%7d %7d path %7b animation: pulse 1s ease-out infinite; transform-origin: 50%25 50%25; will-change: transform; -webkit-backface-visibility:hidden;%7d %3c/style%3e%3cpath fill='%23fe3b1f' fill-rule='nonzero' d='M299.766 72.868l-53.983 51.453-52.297-51.453h-68.322L68.651 126.85V247.47l177.132 172.914 179.662-173.758V127.695l-58.2-54.827h-67.48zm7.591 26.991h51.453l-42.175 94.47-53.14-52.295 43.862-42.175zm-71.696 275.82l-39.644-39.643 37.113-85.193 40.488 39.644-37.957 85.192zm-21.93-143.392l-37.114 84.348-45.549-43.86 61.575-61.575 21.087 21.087zm70.852 33.74l-76.757-75.915 21.087-48.078 76.757 76.757-21.087 47.235zM134.443 99.858h51.452l24.461 24.462-21.087 47.235-64.948-61.575 10.121-10.122zm-37.957 38.8l9.278-9.277 68.322 64.948-60.73 60.73-16.87-16.025V138.66zm301.124 99.532L269.4 360.497l54.827-123.15 5.904-12.652h67.48v13.496zm0-41.33h-55.67l35.426-78.445 20.244 19.4v59.044z'/%3e%3c/svg%3e");
    --image-close-x: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLW1pdGVybGltaXQ9IjIiIGNsaXAtcnVsZT0iZXZlbm9kZCIgdmlld0JveD0iMCAwIDMwIDMwIj4KICAgIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0xNC44NSAxMy40MzVMMS40MTMgMCAwIDEuNDE1IDEzLjQzNSAxNC44NSAwIDI4LjI4NWwxLjQxNCAxLjQxNEwxNC44NSAxNi4yNjRsMTMuNDM1IDEzLjQzNSAxLjQxNC0xLjQxNEwxNi4yNjMgMTQuODUgMjkuNjk4IDEuNDE1IDI4LjI4NCAwIDE0Ljg1IDEzLjQzNXoiLz4KICA8L3N2Zz4=');
    --image-play-button: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGwtcnVsZT0iZXZlbm9kZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLW1pdGVybGltaXQ9IjIiIGNsaXAtcnVsZT0iZXZlbm9kZCIgdmlld0JveD0iMCAwIDkwIDkwIj4KICAgIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik00NC42NC0uMDAzQzIwLS4wMDMgMCAxOS45OTcgMCA0NC42MzdjMCAyNC42MyAyMCA0NC42MyA0NC42NCA0NC42MyAyNC42MyAwIDQ0LjYzLTIwIDQ0LjYzLTQ0LjYzIDAtMjQuNjQtMjAtNDQuNjQtNDQuNjMtNDQuNjR6bTAgMi45MmMyMy4wMiAwIDQxLjcxIDE4LjY5IDQxLjcxIDQxLjcyIDAgMjMuMDItMTguNjkgNDEuNzEtNDEuNzEgNDEuNzEtMjMuMDMgMC00MS43Mi0xOC42OS00MS43Mi00MS43MSAwLTIzLjAzIDE4LjY5LTQxLjcyIDQxLjcyLTQxLjcyem0xNy40IDQxLjcybC0yNi44IDE3LjEzdi0zNC4yN2wyNi44IDE3LjE0eiIvPgogIDwvc3ZnPg==');
    --image-mission-background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjIuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAxNjE5LjU2Njg5IDQzMi4xMzA2MiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTYxOS41NjY4OSA0MzIuMTMwNjI7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7b3BhY2l0eTowLjA1O30KCS5zdDF7ZmlsbDojRkZGRkZGO30KPC9zdHlsZT4KPGcgY2xhc3M9InN0MCI+Cgk8cGF0aCBjbGFzcz0ic3QxIiBkPSJNODQwLjIxMTU1LDBjLTgxLjA0MTk5LDg2Ljk4OTI2LTE2NS44NzY3NywxNzguMDQ5NTYtMjU0LjU1MTY0LDI3My4yMzE2OQoJCUM1NDEuNTkxOTgsMTc2LjM1MDgzLDUwMS41MDI1Myw4OC4yMTYwNiw0NjEuMzc2MDEsMEgyOTIuMzcyOTZjNjQuMjgwNjQsMTQ0LjcxODUxLDEyOC4xMTQyLDI4OC40MzA0MiwxOTEuOTQyNjMsNDMyLjEzMDYyCgkJaDE4MS42Nzg1YzkwLjk3NTY1LTk3LjYyNjk1LDE2OS43ODUzNC0xODIuNTY5MzQsMjU3LjIzMTg3LTI3Ni40MDc3MWM0MS42ODIzNyw5Ni4zNDM5OSw4MC42Mzc3NiwxODYuMzg0NjQsMTE5LjU4NTQ1LDI3Ni40MDc3MQoJCWgxNzcuMTkyODdjLTM2LjgxNDA5LTg1LjYwNDM3LTczLjUxNzA5LTE3MS4yNTY1OS0xMTAuNDkwODQtMjU2Ljc5MTk5Yy0xOS4xOTg3My00NC40MTQ0My0zOC44ODAzNy04OC42MjAxMi01OC44NTUxLTEzNC4wODM1CgkJYzExLjQ3MzUxLTEwLjk4OTc1LDI5LjY3MTg4LTI4LjcyMDcsNDIuNzUxMS00MS4yNTUxM0g4NDAuMjExNTV6Ii8+Cgk8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTI3OC4wMjE5NywwYzEwOC45ODkxNCwxMDQuNzY4MTksMjEzLjYzNTg2LDIwNS4zNjIwNiwzMjAuMjQ2ODMsMzA3Ljg0Mzc1CgkJYy00NS4zMTUwNiw0NC43MDgyNS04NS42NTk5MSw4NC41MTI3LTEyNS45NzM2MywxMjQuMjg2ODdoMTQ3LjI3MTczYzAtMTExLjE0MDk5LDAtMjIyLjI4MTk4LDAtMzMzLjQyMzEKCQlDMTU4OC4xNjA2NCw2NS40NzEzMSwxNTU2Ljc2MDg2LDMyLjI0MTk0LDE1MjYuMjk0MTksMEgxMjc4LjAyMTk3eiIvPgoJPHBhdGggY2xhc3M9InN0MSIgZD0iTTAuMTc4MzIsMjMzLjI3NDE3YzAsNjMuOTE5MTktMC40MTA3NywxMzAuMDE0ODksMC4wMTQ1OSwxOTMuOTMxMTVsNi4yOTYyLDQuOTI1MjloMTk5LjI2MTYKCQlDMTM3LjIyNjU5LDM2NS44NDUwOSw2OC43MDI0OCwyOTkuNTU5ODEsMC4xNzgzMiwyMzMuMjc0MTd6Ii8+CjwvZz4KPC9zdmc+Cg==');

    /* Easing */
    --ease-out-expo: cubic-bezier(0.19, 1, 0.22, 1);

    /* Scrollbar width */
    --body-scrollbar-width-hidden: 0px;

    /* Color Theme */
    --color-theme: ${THEME_COLORS.greyAccented.accent.default};
    --color-theme-inverse: ${THEME_COLORS.greyAccented.accent.inverse};
    --color-theme-dark: ${THEME_COLORS.greyAccented.accent.dark};
    --color-theme-light: ${THEME_COLORS.greyAccented.accent.light};
  }

  @media (min-width: ${mainTheme.breakpoints.sm}) {
    :root {
      /* Dimensions */
      --form-side-padding: 5.25rem;
      --modal-padding: 4rem;
    }
  }

  @media (min-width: ${mainTheme.breakpoints.md}) {
    :root {
      /* Dimensions */
      --main-container-width: 1302px;
    }
  }

  // media query copied from THL.org
  @media (min-width: 1280px) {
    :root {
      --height-nav: ${dimensions.largeHeightNav.value}${dimensions.largeHeightNav.unit};
    }
  }

  body {
    font-family: var(--font-primary);
    background: var(--color-theme-base);
    color: var(--color-theme-base-inverse);
    min-height: 100vh;
    line-height: 1.65;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    overflow-x: clip;

    &.no-scroll, &.no-scroll-gutter {
      height: 100vh;
      overflow: hidden;
    }

    &.no-scroll-gutter {
      padding-right: var(--body-scrollbar-width-hidden);
    }
  }

  // Accessible helper classes

  // Use visually-hidden if you want something to be hidden from sighted users, but available to screen readers
  .visually-hidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  // Use totally-hidden to hide content from sighted users and screen readers
  .totally-hidden {
    display: none;
  }

  *:focus-visible {
    ${focusOutline};
  }
`;
